import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import useTranslations from '@/i18n/useTranslations';
import XIcon from '@/shared-components/icons/components/public/XIcon';

import { useArtGamesRadio } from './radioContext';
import { RADIO_TRACKS } from './tracks';
import useRadioDockPosition from './useRadioDockPosition';

import './RadioControl.css';

function RadioIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
      <path d="M8.5 8.5a5 5 0 0 0 0 7M15.5 8.5a5 5 0 0 1 0 7" />
      <path d="M5.4 5.4a9.3 9.3 0 0 0 0 13.2M18.6 5.4a9.3 9.3 0 0 1 0 13.2" />
    </svg>
  );
}

export default function RadioControl() {
  const t = useTranslations('common.newspaper.art_games.radio');
  const { status, trackId, play, pause, selectTrack } = useArtGamesRadio();
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const tracksRef = useRef(null);
  const dock = useRadioDockPosition();
  const titleId = useId();
  const panelId = useId();
  const isPlaying = status === 'playing' || status === 'switching';
  const isTrackBusy = status === 'loading' || status === 'switching';

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(290, window.innerWidth - 24);
      const panelHeight = panelRef.current?.getBoundingClientRect().height || 0;
      const below = rect.bottom + 10;
      const above = rect.top - panelHeight - 10;
      const top = below + panelHeight <= window.innerHeight - 12
        ? below
        : above >= 12 ? above : Math.max(12, window.innerHeight - panelHeight - 12);
      setPosition({
        top,
        left: Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12)),
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    panelRef.current?.focus();
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isMobile, open]);

  useLayoutEffect(() => {
    if (!open) return;
    const list = tracksRef.current;
    const current = list?.querySelector('[aria-pressed="true"]');
    if (!current) return;
    list.scrollTop = current.offsetTop - list.offsetTop -
      (list.clientHeight - current.offsetHeight) / 2;
  }, [open, trackId]);

  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
    if (event.key !== 'Tab') return;
    const focusable = [...panelRef.current.querySelectorAll('button:not(:disabled)')];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (document.activeElement === panelRef.current) {
      event.preventDefault();
      (event.shiftKey ? last : first)?.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const statusText = {
    ready: t('ready'),
    loading: t('loading'),
    playing: t('playing'),
    switching: t('switching'),
    paused: t('paused'),
    blocked: t('blocked'),
    error: t('error'),
  }[status] || t('ready');

  const trigger = (
    <button
      ref={buttonRef}
      type="button"
      className={`art-games-radio__trigger${isPlaying ? ' is-playing' : ''}${isMobile ? ' is-floating' : ''}`}
      style={isMobile ? { '--art-games-radio-dock-y': `${dock.fraction * 100}dvh` } : undefined}
      onPointerDown={isMobile ? dock.onPointerDown : undefined}
      onPointerMove={isMobile ? dock.onPointerMove : undefined}
      onPointerUp={isMobile ? dock.onPointerUp : undefined}
      onPointerCancel={isMobile ? dock.onPointerCancel : undefined}
      onClick={event => {
        if (isMobile && dock.consumeDragClick(event)) return;
        setOpen(true);
      }}
      aria-label={t('open')}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? panelId : undefined}
    >
      <RadioIcon />
      {isPlaying && <span className="art-games-radio__live-dot" aria-hidden="true" />}
    </button>
  );

  return (
    <>
      {isMobile ? createPortal(trigger, document.body) : trigger}
      {open && createPortal(
        <div className="art-games-radio__overlay" onMouseDown={event => {
          if (event.target === event.currentTarget) close();
        }}>
          <section
            id={panelId}
            ref={panelRef}
            className="art-games-radio__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            style={position}
          >
            <div className="art-games-radio__heading">
              <div>
                <h2 id={titleId}>{t('title')}</h2>
                <p aria-live="polite">{statusText}</p>
              </div>
              <button type="button" className="art-games-radio__close" onClick={close} aria-label={t('close')}>
                <XIcon className="x-icon--interactive" aria-hidden="true" />
              </button>
            </div>
            <div ref={tracksRef} className="art-games-radio__tracks" role="group" aria-label={t('tracks')}>
              {RADIO_TRACKS.map((track, index) => (
                <button
                  key={track.id}
                  type="button"
                  className={`art-games-radio__track${trackId === track.id ? ' is-current' : ''}`}
                  onClick={() => selectTrack(track.id)}
                  aria-pressed={trackId === track.id}
                  disabled={isTrackBusy}
                >
                  <span className="art-games-radio__track-number">{String(index + 1).padStart(2, '0')}</span>
                  <span>{track.title}</span>
                  {trackId === track.id && <span className="art-games-radio__track-current">{t('current')}</span>}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="art-games-radio__transport"
              onClick={isPlaying ? pause : play}
              disabled={status === 'loading'}
            >
              <span aria-hidden="true">{isPlaying ? 'Ⅱ' : '▶'}</span>
              {isPlaying ? t('pause') : t('play')}
            </button>
          </section>
        </div>,
        document.body
      )}
    </>
  );
}
